import { consoleWarn } from 'common/utilities/browser/consoleLog/consoleLog';
import { sleep } from 'common/utilities/timeout/timeout';
import { isRequestAborted } from 'core/api/apiHelpers';
import { Configuration } from 'core/api/coreApiClient';

import CommandUrlFormatterMiddleware from './middlewares/commandUrlFormatterMiddleware';
import LoggerMiddleware from './middlewares/loggerMiddleware';
import RequestFormatterMiddleware, { API_URL_IDENTIFIER } from './middlewares/requestFormatterMiddleware';
import ResponseHandlerMiddleware from './middlewares/responseHandlerMiddleware';
import RetryMiddleware from './middlewares/retryMiddleware';

interface Options {
  pathOverride?: string;
  retry?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Class<T> = new (...args: any[]) => T;

/**
 * Retry fetching an URL up to three times
 */
export async function fetchWithRetry<T, U>(
  fn: (api: T) => Promise<U>, apiInstance: T, retry = 0,
): Promise<U> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requestFormatterMiddleware: RequestFormatterMiddleware = (apiInstance as any).middleware[1];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const retryMiddleware: RetryMiddleware = (apiInstance as any).middleware[2];

  retryMiddleware.retryNumber = retry;

  try {
    return await fn(apiInstance);
  } catch (error) {
    if (!retryMiddleware.retryOnFailure) {
      return Promise.reject();
    }

    if (isRequestAborted(error)) {
      return Promise.reject(error);
    }

    consoleWarn(`FETCH RETRY - ${requestFormatterMiddleware.finalUrl || ''}: ${JSON.stringify(error)}`);
    await sleep((retry + 1) * 50);
    return fetchWithRetry(fn, apiInstance, retry + 1);
  }
}

/**
 * Wrap all data analyzer API calls.
 * - initialize and return API endpoints instances
 * - post call middleware: catch and show generic SPA API messages / redirects
 * - error call: catch and show generic SPA API messages / redirects
 */
export const openAPIWrapper = <T>(Api: Class<T>, options?: Options) => {
  return async<U> (fn: (api: T) => Promise<U>) => {
    const apiInstance = new Api(new Configuration({
      basePath: API_URL_IDENTIFIER,
      // The order of middlewares is important:
      // - pre/post processing is done in a specific order
      // - ResponseHandlerMiddleware should be the last one as it will stop the propagation
      // - other middlewares are picked from the list in fetchWithRetry
      middleware: [
        new CommandUrlFormatterMiddleware(options?.pathOverride),
        new RequestFormatterMiddleware(),
        new RetryMiddleware(),
        new LoggerMiddleware(),
        new ResponseHandlerMiddleware(),
      ],
    }));

    if (options?.retry) {
      return fetchWithRetry<T, U>(fn, apiInstance);
    }
    try {
      return await fn(apiInstance);
    } catch (e) {
      // Generic API responses are handled within the middleware/post action
      return await Promise.reject(e);
    }
  };
};
